/**
 * アニメーション時相ユーティリティ（happa v1.1 / Phase J）。
 *
 * DETONATE プレビューと書き出しの両方で使う「進行秒数」を一元化する薄い
 * ラッパー。Phase J 以前は `TOTAL_DURATION_MS = 3000` 定数だったが、
 * ユーザが UI から選べる `state.detonateDuration`（秒）に差し替わった。
 * 定数はそのまま `default = 2` の参照用として残す運用もできたが、複数個所
 * に `* 1000` を書くと単位揺れが起きるため helper として集約した。
 *
 * v1.0 は `intact → burst` の二相モデルで `crackProgress` /
 * `burstDisplacement` / `cellDisplacement` の複合変換を持っていたが、
 * v1.1 では spec §3.3 の単相モデル（`easeOutExpo(progress)`）に統合された。
 * 変位カーブは `src/core/easing.ts#easeOutExpo` を直接 import する運用
 * （呼び出し側: render / export）。本ファイルからは再 export しない
 * （単一の真実源を保つため）。
 *
 * 設計参照:
 * - `docs/happa-designtool-v1.1-spec.md` §3.3（二相レンダリング進行度）
 * - `docs/happa-designtool-v1.1-spec.md` §A.3（easeOutExpo 進捗テーブル）
 * - Phase J（Export Timing Controls、`prePulseDuration` / `detonateDuration`）
 */

import type { AppState } from '../state/types';

/**
 * `detonateDuration` の下限（秒）。`0` / 負値が入っても division-by-zero や
 * 逆再生に陥らないよう、ホットパスの `tick` で clamp する保険値。UI は
 * 0.5 秒以上しか選ばせない想定だが、state 層の契約としては緩い。
 */
const MIN_DETONATE_DURATION_S = 0.05;

/**
 * DETONATE アニメの総再生時間をミリ秒で返す。
 *
 * Phase J で `state.detonateDuration`（秒、UI では 5/4/3/2/1/0.5 の 6 段階）
 * に差し替わった。`main.ts` の `tick` / `startDetonation` と
 * `src/export/webmExport.ts` の Detonate / Full Story プランがこの関数を
 * 参照する（Phase K2 Step 2 で gif.ts は撤去済み）。
 *
 * 下限 50ms で clamp するのは、DETONATE ボタン連打中にレースで負値 state が
 * 入り込むような最悪ケースの保険。通常入力では影響しない。
 *
 * @param state 現在の AppState スナップショット。
 * @returns 爆破尺（ミリ秒、>= 50ms）。
 */
export function computeDetonateDurationMs(state: AppState): number {
  const seconds = Math.max(MIN_DETONATE_DURATION_S, state.detonateDuration);
  return seconds * 1000;
}
